import { BryntumGantt, BryntumGanttProjectModel, BryntumToolbar } from '@bryntum/gantt-react';
import { customTaskTooltip, projectConfig, useGanttConfig } from './BryntumConfig';
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GanttTask, Project, RenewalChildRecord, RenewalRecord, Resource } from 'models/Renewals';
import '@bryntum/gantt/gantt.stockholm.css';
import { Badge, Button, Card, Col, InputRef, notification, Row, Spin } from 'antd';
import { PlusOutlined, TableOutlined } from '@ant-design/icons';
import './bryntum-gantt.scss';
import { ANONYMOUS_CLIENT_NAME, RENEWAL_RECORD_TYPE } from 'shared/constants';
import { Auth0User } from 'types/global';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { profileSwitchState, selectedTopNavKeyState } from 'recoil/atoms';
import { DealModal } from 'components/deal-modal/DealModal';
import { useProjectApi } from 'api/project';
import { usePortfolioApi } from 'api/usePortfolio';
import { useAdminApi } from 'api/admin';
import { useAddTasks, useDeleteDealTask, useUpdateTask } from 'api/contract';
import { EditTasksPayload, Task as ContractTask } from 'types/Contract';
import { DealTypeModal } from 'components/deal/DealTypeModal';
import { RenewalGanttChartToolbarItems } from './RenewalGanttChartToolbar';
import { useDealHelper } from 'hooks/useDealHelper';
import AddDealTaskForm from './AddDealTaskForm';
import { EditTaskModal } from './EditDealTaskModal';
import {
  AssignmentModel,
  AssignmentModelConfig,
  ColumnStore,
  Gantt,
  StateProvider,
  TaskTooltipConfig
} from '@bryntum/gantt';
import { GanttChartMode } from 'sb/shared/constants';
import { useNavigate } from 'react-router-dom';
import { PortfolioCompany } from '../../../models/Portfolio';
import {
  ACLCheck,
  FieldSorterDate,
  FieldSorterNumber,
  FormatCurrency,
  hasNestedChildren
} from '../../../shared/Helpers';
import { useCategoriesApi } from 'api/useCategories';
import { collectAllDeals, dealMatchesZone, RenewalGanttChartCardTitle } from './RenewalGanttChartCardTitle';
import { useRegisterChatContext } from 'components/client-chat/pageContext';
import moment from 'moment-timezone';
import { FieldSorter } from 'sb/shared/Helpers';
import { useCustomFieldApi } from 'api/custom_field';
import { useClientApi, ClientPortfolio } from 'api/useClient';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CalendarStatus,
  DEFAULT_SMART_CALENDAR_PREFS,
  GroupView,
  SmartCalendarPrefs,
  expiresInPresetToDate,
  readSmartCalendarPrefs,
  smartCalendarPrefsKey
} from './smartCalendarPrefs';
import { HiddenDealsSection, type HiddenCalendarDeal } from './HiddenDealsSection';
import { useNewDeal } from 'api/useNewDeal';
import { hasStoredZoomLevel, shouldAutoFitZoom } from './smartCalendarZoom';

class GanttErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(_error: unknown) {
    try {
      const retryFlagKey = 'gantt-error-retry';
      const hasRetried = sessionStorage.getItem(retryFlagKey) === 'true';

      // Clear any Bryntum state persisted in localStorage
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('bryntum-state:')) {
          localStorage.removeItem(key);
        }
      }

      if (!hasRetried) {
        sessionStorage.setItem(retryFlagKey, 'true');
        window.location.reload();
      } else {
        // Avoid infinite reload loops if the error isn't resolved by clearing state
        sessionStorage.removeItem(retryFlagKey);
      }
    } catch (e) {
      // no-op
    }
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong. Please refresh the page.</div>;
    }
    return this.props.children as any;
  }
}

interface Props {
  id?: number | string;
  renewalsObject: 'client' | 'supplier' | 'npi';
  onClose?: () => void;
  renewalTimeframe?: string;
  mode?: GanttChartMode;
}

const CUSTOM_FIELD_COLUMN_GROUP_ID = 'col-customFields';
const CUSTOM_FIELD_COLUMN_PREFIX = `${CUSTOM_FIELD_COLUMN_GROUP_ID}-`;
const STALE_GANTT_COLUMN_IDS = [CUSTOM_FIELD_COLUMN_GROUP_ID, 'col-tcvAgg'];

interface CustomFieldColumnOrderState {
  childIds: string[];
  groupHidden?: boolean;
  hiddenChildIds?: string[];
  previousSiblingId?: string;
  nextSiblingId?: string;
}

const getCustomFieldColumnId = (customField: any): string => {
  return `${CUSTOM_FIELD_COLUMN_PREFIX}${customField.key ?? customField.label}`;
};

const getColumnRecords = (columnsStore: any): any[] => {
  if (Array.isArray(columnsStore)) return columnsStore;
  if (Array.isArray(columnsStore?.records)) return columnsStore.records;
  if (Array.isArray(columnsStore?.allRecords)) return columnsStore.allRecords;
  if (typeof columnsStore?.toArray === 'function') return columnsStore.toArray();
  return [];
};

const isColumnHidden = (column: any): boolean => {
  if (column?.hidden !== undefined) return column.hidden === true;
  if (column?.isVisible !== undefined && typeof column.isVisible !== 'function') return column.isVisible === false;
  return false;
};

/**
 * Resolves the column ids a persisted Bryntum layout may never restore for the current user.
 *
 * Version-dependent because the two calendar flows support different columns: v2 (`read:early_access`)
 * replaces `col-fmvReportLink` with `col-reports`, while v1 has neither `col-reports` nor
 * `col-proposals`.
 *
 * `col-dealSizeTrend` is listed for v2 only (VE-2225): v2 users who saved a layout while the
 * column was still visible must not have it restored, but it is still supported on v1, so it
 * must NOT go in the always-stale list or every v1 layout would silently lose it.
 *
 * `col-alert` is listed for v2 only on the same basis (VE-2471). Gating it out of
 * `ganttChartColumnConfig` is only half the fix: without this entry, a v2 user who saved a
 * layout while the bell was live has it restored, and the bell sends the deal uuid to the
 * contract-keyed endpoint again — the same 400. It stays supported on v1.
 *
 * Pure and exported so the list is unit-testable without mounting the Gantt.
 * @param {boolean} hasEarlyAccess - Whether the user holds `read:early_access` (i.e. sees v2).
 * @returns {string[]} Column ids to strip from persisted state, on top of the always-stale ones.
 */
export const resolveStaleColumnIds = (hasEarlyAccess: boolean): string[] =>
  hasEarlyAccess ? ['col-fmvReportLink', 'col-dealSizeTrend', 'col-alert'] : ['col-proposals', 'col-reports'];

/**
 * Strips columns that must never be restored from a persisted Bryntum layout.
 *
 * Bryntum re-applies the user's saved column layout from localStorage, so removing a column id
 * from `ganttChartColumnConfig` does not stop a layout saved while that column was live from
 * bringing it back. Every parked or access-gated id therefore has to be filtered out here as
 * well. Pure and exported so the strip list is unit-testable without mounting the Gantt.
 * @param {any[]} columns - The persisted column layout entries.
 * @param {string[]} extraStaleColumnIds - Caller-supplied ids to strip on top of the parked ones.
 * @returns {any[]} The surviving layout entries, with the persisted header text dropped.
 */
export const sanitizeGanttColumns = (columns: any[] = [], extraStaleColumnIds: string[] = []) => {
  const staleColumnIds = [...STALE_GANTT_COLUMN_IDS, ...extraStaleColumnIds];
  return columns
    .filter((column: any) => {
      const columnId = String(column?.id || '');
      return !staleColumnIds.some((staleColumnId) => columnId.includes(staleColumnId));
    })
    .map((column: any) => {
      const layoutState = { ...column };
      delete layoutState.text;
      return layoutState;
    });
};

const getCustomFieldSiblingId = (columns: any[], startIndex: number, direction: -1 | 1): string | undefined => {
  for (let index = startIndex + direction; index >= 0 && index < columns.length; index += direction) {
    const columnId = String(columns[index]?.id || '');
    if (columnId && !columnId.startsWith(CUSTOM_FIELD_COLUMN_GROUP_ID)) return columnId;
  }

  return undefined;
};

const getCustomFieldColumnOrderState = (columns: any[] = []): CustomFieldColumnOrderState | null => {
  const groupIndex = columns.findIndex((column) => column?.id === CUSTOM_FIELD_COLUMN_GROUP_ID);
  const groupColumn = groupIndex >= 0 ? columns[groupIndex] : undefined;
  const childColumns = getColumnRecords(groupColumn?.children).filter((column: any) =>
    String(column?.id || '').startsWith(CUSTOM_FIELD_COLUMN_PREFIX)
  );
  const childIds = childColumns.map((column: any) => String(column?.id || ''));
  const hiddenChildIds = childColumns
    .filter((column: any) => isColumnHidden(column))
    .map((column: any) => String(column?.id || ''));

  if (!groupColumn && childIds.length === 0) return null;

  return {
    childIds,
    groupHidden: groupColumn ? isColumnHidden(groupColumn) : undefined,
    hiddenChildIds,
    previousSiblingId: groupIndex >= 0 ? getCustomFieldSiblingId(columns, groupIndex, -1) : undefined,
    nextSiblingId: groupIndex >= 0 ? getCustomFieldSiblingId(columns, groupIndex, 1) : undefined
  };
};

const readCustomFieldColumnOrderState = (storageKey: string): CustomFieldColumnOrderState | null => {
  try {
    const rawState = localStorage.getItem(storageKey);
    return rawState ? (JSON.parse(rawState) as CustomFieldColumnOrderState) : null;
  } catch (_e) {
    return null;
  }
};

const applyCustomFieldColumnOrder = (customFields: any[], state: CustomFieldColumnOrderState | null): any[] => {
  if (!state?.childIds.length) return customFields;

  const orderByColumnId = new Map(state.childIds.map((columnId, index) => [columnId, index]));
  return [...customFields].sort((left, right) => {
    const leftOrder = orderByColumnId.get(getCustomFieldColumnId(left)) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = orderByColumnId.get(getCustomFieldColumnId(right)) ?? Number.MAX_SAFE_INTEGER;
    return leftOrder - rightOrder;
  });
};

const getColumnStoreRecords = (columnsStore: any): any[] => {
  return getColumnRecords(columnsStore);
};

const getCustomFieldColumnInsertIndex = (
  columns: any[],
  state: CustomFieldColumnOrderState | null
): number | undefined => {
  if (!state) return undefined;

  if (state.nextSiblingId) {
    const nextSiblingIndex = columns.findIndex((column) => column?.id === state.nextSiblingId);
    if (nextSiblingIndex >= 0) return nextSiblingIndex;
  }

  if (state.previousSiblingId) {
    const previousSiblingIndex = columns.findIndex((column) => column?.id === state.previousSiblingId);
    if (previousSiblingIndex >= 0) return previousSiblingIndex + 1;
  }

  return undefined;
};

export const RenewalGanttChart: React.FC<Props> = ({
  id,
  renewalsObject,
  onClose,
  renewalTimeframe,
  mode = GanttChartMode.OWN_CALENDER
}) => {
  // REFERENCES
  const ganttRef = useRef<BryntumGantt>(null);
  const projectRef = useRef<BryntumGanttProjectModel>(null);
  const vendorFilterRef = createRef<InputRef>();
  useRef<any>(null);

  // PERSISTED TOOLBAR SELECTIONS (VE-2259, corrected in VE-2605)
  // The key is null until the Auth0 subject is known, so nothing is read or written while this
  // session is anonymous. VE-2259 instead read synchronously at mount and fell back to a shared
  // `anonymous` bucket, on the assumption that App's route gate guaranteed a subject by then. It
  // does not: `handleLogout` clears the cached `permissions`, `ACLCheck` then returns false, and
  // the gate's `!isAuthenticated && ACLCheck(...)` stops holding — so on the first load after any
  // logout the calendar mounted with no subject, read the shared bucket, and handed one user's
  // filters to the next (VE-2605).
  // Read via `useAuth0` rather than the wrapped `MyAuth0Context`, which lives in `src/index.tsx`:
  // importing it here would add another module-graph edge from this component to the app
  // bootstrap, whose top-level env validation throws outside a configured environment. The E2E
  // mock provider supplies both contexts, so either resolves the same user.
  const { user } = useAuth0();
  const prefsStorageKey = useMemo(() => smartCalendarPrefsKey(user?.sub, mode), [user?.sub, mode]);
  const [initialPrefs, setInitialPrefs] = useState<SmartCalendarPrefs>(DEFAULT_SMART_CALENDAR_PREFS);
  // Gates the renewals fetch below. Status and Expires In are both fetch dependencies, and that
  // effect fires on an uncancelled 1s timer, so fetching before the restore lands would race it —
  // last response wins, which can be the one built from defaults. Waiting also removes the
  // throwaway request that a fetch-then-restore-then-refetch sequence would cost.
  const [prefsRestored, setPrefsRestored] = useState(false);

  // LOCAL STATE
  const [tasks, setTasks] = useState<Array<RenewalRecord>>([]);
  const [_tasks, _setTasks] = useState<Array<RenewalRecord>>([]);
  const [project, setProject] = useState<Project | undefined>(undefined);
  const [dealModalOpen, setDealModalOpen] = useState(false);
  const [task, setTask] = useState<undefined | GanttTask>(undefined);
  const [resources, setResources] = useState<Array<Resource>>([]);
  const [isDealTypeModalOpen, setIsDealTypeModalOpen] = useState(false);
  const [assignments, setAssignments] = useState<AssignmentModel[] | Partial<AssignmentModelConfig>[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<CalendarStatus>(initialPrefs.status);
  const { SUPPLIER, SUPPLIER_DEAL, DEAL } = RENEWAL_RECORD_TYPE;
  const [clientName, setClientName] = useState<string | undefined>(undefined);
  const [customFields, setCustomFields] = useState<any[]>([]);
  const navigate = useNavigate();
  const [portfolioCompanies, setPortfolioCompanies] = useState<PortfolioCompany[]>([]);
  const [isGrandFather, setIsGrandFather] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [expiresIn, setExpiresIn] = useState<Date | undefined>(expiresInPresetToDate(initialPrefs.expiresIn));
  const [clientPortfolio, setClientPortfolio] = useState<ClientPortfolio[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [groupView, setGroupView] = useState<GroupView>(initialPrefs.groupView);
  // Deals in the current window with `show_on_smart_calendar = false`. Populated from the
  // deal-table calendar's `hiddenDeals` (client mode only); the legacy payload has no such key, and the
  // portfolio calendar honours the flag without exposing a Hidden Deals workflow.
  const [hiddenDeals, setHiddenDeals] = useState<HiddenCalendarDeal[]>([]);

  // Restore this user's saved selections as soon as their subject resolves. Runs once per bucket:
  // re-reading later would overwrite choices the user has made since. The card title reseeds its
  // own five filters by remounting on `prefsStorageKey` (see `key` on the element below), since
  // those live in its state and only initialise at mount.
  useEffect(() => {
    if (!prefsStorageKey || prefsRestored) return;

    const saved = readSmartCalendarPrefs(prefsStorageKey);
    setInitialPrefs(saved);
    setStatus(saved.status);
    setExpiresIn(expiresInPresetToDate(saved.expiresIn));
    setGroupView(saved.groupView);
    setPrefsRestored(true);
  }, [prefsStorageKey, prefsRestored]);

  // RECOIL STATE
  const profileSwitch = useRecoilValue(profileSwitchState);
  const setSelectedTopNavKey = useSetRecoilState(selectedTopNavKeyState);
  useEffect(() => setSelectedTopNavKey('dealCalendar'), [setSelectedTopNavKey]);

  // Register Smart Calendar's chat-page context. Lives here (not in the parent
  // page) because `_tasks` — the source of truth for contract/red-zone counts —
  // is local state of this component. The registry is call-site-agnostic; the
  // drawer reads whichever component publishes most recently.
  const chatContext = useMemo(() => {
    const root = _tasks[0];
    if (!root?.children?.length) return null;
    const allDeals = collectAllDeals(root.children as RenewalChildRecord[]);
    const redZoneCount = allDeals.filter((d) => dealMatchesZone(d, 'red')).length;
    return {
      pageId: 'smart-calendar',
      pageTitle: 'Smart Calendar™',
      signals: { contractCount: allDeals.length, redZoneCount }
    };
  }, [_tasks]);
  useRegisterChatContext(() => chatContext);

  // API
  const { fetchRenewalsByMode } = useProjectApi();
  const { fetchPortfolioCompanies } = usePortfolioApi();
  const { getClientPortfolio } = useClientApi();
  const { getMyCompanyUsers } = useAdminApi();
  const { getAllCategories } = useCategoriesApi();
  const { dealIdToAddTasks, clearDealIdToAddTasks, dealIdToEditTask, clearDealIdToEditTask } = useDealHelper();
  const { fetchCustomFields } = useCustomFieldApi();
  const { updateDeal } = useNewDeal();

  // Task CRUD
  const { mutate: addTasksToDeal } = useAddTasks({
    onSuccess: async () => {
      notification.success({
        message: 'Tasks added successfully'
      });
      clearDealIdToAddTasks();
      await handleFetchRenewals();
    },
    onError: () => {
      notification.error({
        message: 'Error while adding tasks'
      });
      clearDealIdToAddTasks();
    }
  });

  const { mutate: updateTask } = useUpdateTask({
    onSuccess: async () => {
      notification.success({
        message: 'Task updated successfully'
      });
      clearDealIdToEditTask();
      await handleFetchRenewals();
    },
    onError: () => {
      notification.error({
        message: 'Error while updating task'
      });
      clearDealIdToEditTask();
    }
  });

  const { mutate: deleteDealTask } = useDeleteDealTask({
    onSuccess: async () => {
      notification.success({
        message: 'Task deleted successfully'
      });
      clearDealIdToEditTask();
      await handleFetchRenewals();
    },
    onError: () => {
      notification.error({
        message: 'Error while deleting task'
      });
      clearDealIdToEditTask();
    }
  });

  const handleAddTasks = (
    dealId: number,
    formValues: {
      tasks: ContractTask[];
    }
  ) => {
    const { tasks } = formValues;
    const payload = tasks.map((t) => {
      return {
        name: t.name,
        duration: t.duration,
        assignments: t.assignments,
        comments: t.comments || ''
      };
    });

    addTasksToDeal({
      dealId,
      data: payload
    });
  };

  const handleEditTask = (dealId: number, taskId: number, data: EditTasksPayload) => {
    updateTask({
      dealId,
      taskId,
      data
    });
  };

  const handleDeleteTask = async () => {
    if (dealIdToEditTask?.dealId && dealIdToEditTask?.taskId) {
      deleteDealTask({ dealId: dealIdToEditTask?.dealId, taskId: dealIdToEditTask?.taskId });
    }
  };

  const taskClickHandler = useCallback(
    ({ taskRecord }: any) => {
      const task: GanttTask = taskRecord.data;
      if (!task.id) return;
      if (task.status === 'Calendar') {
        navigate(`/calendar_deals/${task.id}`);
      } else {
        if (mode === GanttChartMode.OWN_CALENDER && (task?.recordType === SUPPLIER_DEAL || task?.recordType === DEAL)) {
          navigate(`/deal_details/${task.id}`);
        }
      }
    },
    [DEAL, SUPPLIER_DEAL, mode, navigate]
  );

  const handleDealModalCancel = () => setDealModalOpen(false);

  const handleDealTypeModalCancel = () => setIsDealTypeModalOpen(false);

  const handlePortfolioSelection = (client_id: number | null) => {
    setSelectedClientId(client_id);
  };

  const removeDealsByDealId = (parentRecord: RenewalRecord, dealId: number, removedRecords: RenewalChildRecord[]) => {
    const recordList: RenewalChildRecord[] = parentRecord.children;
    return recordList.filter((record: RenewalChildRecord) => {
      if (record.recordType == DEAL || record.recordType == SUPPLIER_DEAL) {
        const found: boolean = record.id == dealId;
        if (found) {
          parentRecord.expanded = true;
          removedRecords.push(record);
        }
        return !found;
      } else if (record.recordType == SUPPLIER) {
        record.children = removeDealsByDealId(record, dealId, removedRecords);
        return true;
      } else {
        return true;
      }
    });
  };

  const handleDeleteDeal = (dealId: number) => {
    if (!_tasks[0]) return;
    const removedRecords: RenewalChildRecord[] = [];
    const filteredRecords = removeDealsByDealId(_tasks[0], dealId, removedRecords);
    if (removedRecords.length == 0) {
      notification.warning({ message: 'Record Not found!!' });
    } else {
      const contractsCount = _tasks[0]?.contractsCount ? _tasks[0].contractsCount - 1 : 0;
      const newTask = { ..._tasks[0], children: filteredRecords, contractsCount };
      _setTasks([newTask]);
      // tasks will be auto-updated by the useEffect in RenewalGanttChartCardTitle
    }
  };

  // GANTT CONFIGURATION

  // Get gantt configs
  const ganttConfig = useGanttConfig(mode);

  // Setup gantt state persistance
  const ganttStateId = `gantt-stable-state-${profileSwitch?.id || 'anonymous'}-${mode}`;
  const ganttStatePrefix = `bryntum-state:`;
  const ganttCustomFieldStateKey = `${ganttStatePrefix}${ganttStateId}:customFields`;
  const stateProvider = useMemo(() => StateProvider.setup('local'), []);

  // The Proposals and Report(s) columns are early-access only, Report(s) REPLACES the legacy
  // "Vantage Report" column in early access, and Deal Size Trend is v1-only (VE-2225). Strip
  // whichever set the current user must not see from persisted Bryntum column state, so a
  // previously-saved layout can't restore a column the config already excludes for them.
  // See `resolveStaleColumnIds` for which ids that is per version.
  const earlyAccessStaleColumnIds = useMemo(() => resolveStaleColumnIds(ACLCheck(['read:early_access'])), []);

  // On save, remove dynamic or removed columns that should not be restored from persisted state
  useEffect(() => {
    const detacher = stateProvider.on('save', () => {
      const ganttState = localStorage.getItem(`${ganttStatePrefix}${ganttStateId}`);
      if (ganttState) {
        const ganttStateData = JSON.parse(ganttState);
        const liveColumns = getColumnStoreRecords(ganttRef.current?.instance?.columns);
        const customFieldColumnState = getCustomFieldColumnOrderState(
          liveColumns.length > 0 ? liveColumns : ganttStateData.columns
        );
        if (customFieldColumnState) {
          localStorage.setItem(ganttCustomFieldStateKey, JSON.stringify(customFieldColumnState));
        }
        ganttStateData.columns = sanitizeGanttColumns(ganttStateData.columns, earlyAccessStaleColumnIds);
        localStorage.setItem(`${ganttStatePrefix}${ganttStateId}`, JSON.stringify(ganttStateData));
      }
    });
    return () => detacher?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateProvider, ganttStatePrefix, ganttStateId, ganttCustomFieldStateKey, earlyAccessStaleColumnIds]);

  /**
   * On page load
   */
  useEffect(() => {
    // Zoom to fit the gantt chart
    if (!projectRef || !projectRef.current || !ganttRef || !ganttRef.current) return;

    // VE-2110: fit only a calendar that has no zoom of its own. Bryntum restores `zoomLevel` from
    // the state provider by itself, and this listener lands after that restore — so re-fitting on
    // every refresh is what used to throw the user's zoom away and replace it with a level derived
    // from the window width. `shouldAutoFitZoom` owns the rules; see `smartCalendarZoom.ts`.
    let alreadyFitted = false;

    const detacher = projectRef.current.instance.on({
      refresh: () => {
        // Defer zoomToFit to avoid flushSync warning from Bryntum during React commit phase
        queueMicrotask(() => {
          const gantt = ganttRef.current?.instance;
          if (!gantt) return;

          const shouldFit = shouldAutoFitZoom({
            hasStoredZoom: hasStoredZoomLevel(ganttStateId),
            hasTasks: gantt.taskStore.count > 0,
            alreadyFitted
          });
          if (!shouldFit) return;

          alreadyFitted = true;
          gantt.zoomToFit({
            leftMargin: 50,
            rightMargin: 50
          });
        });
      }
    });

    if (mode == GanttChartMode.OWN_CALENDER) {
      vendorFilterRef.current?.focus();
    }

    // The pre-VE-2110 code never detached, so a remount left the previous mount's listener still
    // fitting the shared project.
    return () => detacher?.();
    // `ganttStateId` is reactive: it is rebuilt from `profileSwitch?.id`, which `SwitchProfileModal`
    // changes in place with no remount. Pinning this effect to `[]` would leave the listener reading
    // the *previous* profile's `bryntum-state:` key. Re-running re-attaches it and clears the
    // `alreadyFitted` latch — a different profile is a different calendar and earns its own fit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ganttStateId]);

  // Fetch the portfolio companies
  useEffect(() => {
    // Get categories
    const fetchCategories = async () => {
      try {
        const response = await getAllCategories(profileSwitch?.id);
        setCategories(response || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories().then();

    // Fetch the company users
    getMyCompanyUsers()
      .then((users: Auth0User[]) => {
        const myCompanyUsers: Array<Resource> =
          users && Array.isArray(users)
            ? users.map((user: Auth0User) => ({
                id: user.user_id,
                name: user.name,
                user_id: user.user_id
              }))
            : [];

        setResources(myCompanyUsers);
      })
      .catch((err) => {
        console.error('Error occurred while fetching company users', err);
      });

    // Fetch the portfolio companies
    if (mode === GanttChartMode.PORTFOLIO) {
      (async (): Promise<void> => {
        try {
          const portfolioCompanies: PortfolioCompany[] | undefined = await fetchPortfolioCompanies(profileSwitch?.id);
          if (Array.isArray(portfolioCompanies)) {
            setPortfolioCompanies(portfolioCompanies);
            setIsGrandFather(hasNestedChildren(portfolioCompanies));
          }

          const portfolio = await getClientPortfolio(profileSwitch?.id);
          if (portfolio) {
            setClientPortfolio(portfolio);
          }
        } catch (e) {
          console.log(e);
        }
      })();
    }
  }, [mode, profileSwitch?.id]);

  useEffect(() => {
    const categoriesColumn = (ganttRef.current?.instance?.columns as any)?.get('categories');
    if (categoriesColumn) {
      const categoriesProps = categoriesColumn?.filterable?.filterField?.html?.props;
      if (categoriesProps) {
        categoriesProps.categories = categories;
      }
    }
  }, [categories, ganttRef]);

  // Fetch the renewals
  const handleFetchRenewals = useCallback(async (): Promise<void> => {
    setLoading(true);

    // Reset the tasks
    setTasks([]);
    _setTasks([]);
    setTask(undefined);
    setHiddenDeals([]);

    // Fetch the renewals
    const renewals = await fetchRenewalsByMode(
      mode,
      status,
      mode === GanttChartMode.PORTFOLIO && selectedClientId ? selectedClientId : profileSwitch?.id,
      expiresIn
    );

    // The deal-table client calendar returns the window's hidden deals beside `tasks`. Only the
    // client calendar surfaces them — the portfolio payload is read for `tasks` alone.
    if (mode === GanttChartMode.OWN_CALENDER && Array.isArray(renewals.hiddenDeals)) {
      setHiddenDeals(renewals.hiddenDeals);
    }

    if (mode === GanttChartMode.PORTFOLIO && selectedClientId) {
      let parentId: string | null = null;
      let childId: string | null = null;
      const tasksFiltered = renewals.tasks.map((grandParent: any) => {
        const children = grandParent.children
          .map((parent: any) => {
            parentId = parent.id === String(selectedClientId) ? parent.id : parentId;
            const children = parent.children
              .map((child: any) => {
                childId = child.id === String(selectedClientId) ? child.id : childId;
                parentId = child.id === String(selectedClientId) ? parent.id : parentId;
                return child;
              })
              .filter((child: any) => child.id === childId || !childId);
            return {
              ...parent,
              children: children
            };
          })
          .filter((parent: any) => parent.id === parentId || !parentId);
        return {
          ...grandParent,
          children: children
        };
      });
      renewals.tasks = tasksFiltered;
    }

    // For PORTFOLIO mode: Remove root node, promote ChildOrgs to root level
    if (mode === GanttChartMode.PORTFOLIO) {
      // Save the root name before flattening (for clientName)
      const rootName = renewals.tasks[0]?.name;

      // Flatten: extract children (ChildOrgs) from root and make them root-level tasks
      const childOrgs = renewals.tasks.flatMap((root: any) => root.children || []);

      // Remove parentId from each ChildOrg so Bryntum treats them as root-level
      childOrgs.forEach((childOrg: any) => {
        delete childOrg.parentId;
      });

      renewals.tasks = childOrgs;

      // Set the client name from the original root
      if (profileSwitch && profileSwitch.anonymous) {
        setClientName(ANONYMOUS_CLIENT_NAME);
      } else {
        setClientName(rootName || '');
      }
    } else {
      // Set the renewals (OWN_CALENDER mode)
      if (profileSwitch && profileSwitch.anonymous) renewals.tasks[0].name = ANONYMOUS_CLIENT_NAME;
      setClientName(renewals.tasks[0].name);
    }
    setProject(renewals.project);
    setAssignments(renewals.assignments);
    // Set custom fields

    setTasks(renewals.tasks);
    _setTasks(renewals.tasks);

    // enforcePublished=true: respect the client master switch — when custom
    // fields aren't published the backend returns [], so the calendar renders
    // neither the custom-field columns nor their values.
    const customFields = await fetchCustomFields(true, profileSwitch?.id, true);
    setCustomFields(customFields?.customFields ?? []);

    setLoading(false);
  }, [mode, profileSwitch, status, expiresIn, selectedClientId]);

  /**
   * Puts a hidden deal back on the Smart Calendar: a one-field `PATCH /deals/:id` flipping
   * `show_on_smart_calendar` to true — the same endpoint, guard and scopes the Edit Deal Metadata form
   * uses, so tenancy is enforced server-side — then a full refetch so the deal moves from the Hidden
   * Deals table into the Gantt. Nothing else about the deal (dates included) is sent.
   * @param {string} dealId - Deal UUID from the hidden-deals row.
   * @returns {Promise<void>} Resolves after the calendar has refetched (or the error has been shown).
   */
  const handleUnhideDeal = async (dealId: string): Promise<void> => {
    const formData = new FormData();
    formData.append('show_on_smart_calendar', 'true');
    try {
      // `updateDeal` rethrows the normalised error from `AxiosErrorHandler`; `undefined` only for a blank id.
      const updated = await updateDeal(dealId, formData);
      if (!updated) return;
      notification.success({ message: 'Deal is back on the Smart Calendar.' });
      await handleFetchRenewals();
    } catch (e) {
      notification.error({
        message: 'Could not unhide the deal',
        description: e instanceof Error ? e.message : 'Please try again.'
      });
    }
  };

  // Add custom fields columns
  useEffect(() => {
    const columnsStore = ganttRef.current?.instance.columns as ColumnStore;
    if (!columnsStore) return;
    const persistedCustomFieldColumnState = readCustomFieldColumnOrderState(ganttCustomFieldStateKey);
    const orderedCustomFields = applyCustomFieldColumnOrder(customFields, persistedCustomFieldColumnState);

    const customFieldsCustom = {
      id: CUSTOM_FIELD_COLUMN_GROUP_ID,
      field: 'customFields',
      text: 'Custom Fields',
      collapsible: true,
      collapseMode: 'toggleAll',
      hidden: persistedCustomFieldColumnState?.groupHidden ?? true,
      autoWidth: true,
      // Make this a group column with dynamic nested columns for each custom field label
      children: orderedCustomFields.map((customField: any) => {
        const columnId = getCustomFieldColumnId(customField);
        return {
          id: columnId,
          text: customField.label,
          type: ['date', 'number'].includes(customField.dataType) ? customField.dataType : undefined,
          autoWidth: true,
          hidden: persistedCustomFieldColumnState?.hiddenChildIds?.includes(columnId) ?? false,
          renderer: ({ record }: any) => {
            const list = record?.data?.customFields;
            if (!Array.isArray(list) || list.length === 0) return '-';
            const item = list.find((f: any) => f?.label === customField.label);
            const value = item?.value;
            if (value === null || value === undefined || value === '') return '-';
            switch (customField.dataType) {
              case 'date':
                return moment(value).format('l');
              case 'boolean':
                return value ? 'Yes' : 'No';
              case 'number':
                return Number(value);
              default:
                return value;
            }
          },
          sortable(a: any, b: any) {
            if (a?.data?.customFields || b?.data?.customFields) {
              console.log('sortable', a?.data?.customFields, b?.data?.customFields);
            }
            const valueA = a?.data?.customFields?.find((f: any) => f.key === customField.key)?.value;
            const valueB = b?.data?.customFields?.find((f: any) => f.key === customField.key)?.value;
            switch (customField?.dataType) {
              case 'date': {
                const ma = valueA ? moment(valueA) : null;
                const mb = valueB ? moment(valueB) : null;
                const da = ma && ma.isValid() ? ma.toDate() : null;
                const db = mb && mb.isValid() ? mb.toDate() : null;
                return FieldSorterDate(da, db);
              }
              case 'number': {
                const toNum = (v: any) => {
                  if (v === null || v === undefined) return null;
                  const cleaned = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.-]/g, ''));
                  return Number.isFinite(cleaned) ? cleaned : null;
                };
                const na = toNum(valueA);
                const nb = toNum(valueB);
                const ca = Number.isFinite(na) ? na : null;
                const cb = Number.isFinite(nb) ? nb : null;
                return FieldSorterNumber(ca, cb);
              }
              default: {
                const sa = typeof valueA === 'string' ? valueA : valueA == null ? null : String(valueA);
                const sb = typeof valueB === 'string' ? valueB : valueB == null ? null : String(valueB);
                return FieldSorter(sa as any, sb as any);
              }
            }
          }
        };
      })
    };

    // Defer column mutations and renderContents to avoid flushSync warning from Bryntum during React commit phase
    queueMicrotask(() => {
      // Remove any existing custom fields group and its children to avoid id collisions
      try {
        const existing = (ganttRef.current?.instance.columns as ColumnStore).get(CUSTOM_FIELD_COLUMN_GROUP_ID) as any;
        if (existing && Array.isArray(existing.children)) {
          for (const child of existing.children) {
            try {
              (ganttRef.current?.instance.columns as ColumnStore).remove(child.id);
            } catch (_e) {
              // ignore
            }
          }
        }
        (ganttRef.current?.instance.columns as ColumnStore).remove(CUSTOM_FIELD_COLUMN_GROUP_ID);
      } catch (_e) {
        // ignore if not present
      }

      // Also proactively remove any would-be child ids derived from current customFields
      for (const cf of customFields) {
        const cid = getCustomFieldColumnId(cf);
        try {
          (ganttRef.current?.instance.columns as ColumnStore).remove(cid);
        } catch (_e) {
          // ignore
        }
      }

      if (orderedCustomFields.length > 0) {
        const insertIndex = getCustomFieldColumnInsertIndex(
          getColumnStoreRecords(columnsStore),
          persistedCustomFieldColumnState
        );

        if (insertIndex !== undefined && typeof (columnsStore as any).insert === 'function') {
          (columnsStore as any).insert(insertIndex, customFieldsCustom);
        } else {
          (ganttRef.current?.instance.columns as ColumnStore).add(customFieldsCustom);
        }
      }

      (ganttRef.current?.instance as Gantt)?.renderContents();
    });
  }, [customFields, ganttCustomFieldStateKey]);

  // Fetch the renewals
  useEffect(() => {
    // Wait for the saved Status / Expires In to land, or the first request goes out with defaults
    // and races the restored one on the uncancelled timer below (VE-2605).
    if (!prefsRestored) return;
    if (renewalsObject !== 'client' && typeof id !== 'number') return;
    // TODO: Delay for 1 second temporarily to avoid race condition
    setTimeout(handleFetchRenewals, 1000);
    // eslint-disable-next-line
  }, [prefsRestored, id, mode, profileSwitch, status, renewalsObject, renewalTimeframe, expiresIn, selectedClientId]);

  /**
   * Computes the total contracts count and aggregated TCV from displayed tasks
   */
  const tasksSummary = useMemo(() => {
    let totalContracts = 0;
    let totalTcv = 0;

    const countDeals = (records: RenewalRecord[]) => {
      records.forEach((record: any) => {
        if (record.children && record.children.length > 0) {
          countDeals(record.children);
        } else {
          totalContracts++;
          totalTcv += record.tcv || 0;
        }
      });
    };

    countDeals(tasks);
    return { totalContracts, totalTcv };
  }, [tasks]);

  const loadingSpinner: React.ReactElement = <div className="text-lg pt-6 center-content">Refreshing calendar ...</div>;

  return (
    <>
      {/* {mode === GanttChartMode.PORTFOLIO && clientPortfolio.length > 0 && (
        <PortfolioSelector clientPortfolio={clientPortfolio} onSelection={handlePortfolioSelection} />
      )} */}
      <Card
        title={
          <RenewalGanttChartCardTitle
            // Remounts once the restore lands, so the filters it holds in state reseed from the
            // restored selections rather than the defaults they mounted with. Gated on
            // `prefsRestored` rather than on the key alone, because `prefsStorageKey` flips a
            // render earlier, while `initialPrefs` is still the defaults.
            key={prefsRestored ? prefsStorageKey : 'awaiting-user'}
            mode={mode}
            _tasks={_tasks}
            startDate={project?.startDate}
            endDate={project?.endDate}
            portfolioCompanies={portfolioCompanies}
            isGrandFather={isGrandFather}
            categories={categories}
            status={status}
            ganttRef={ganttRef}
            setTasks={setTasks}
            vendorFilterRef={vendorFilterRef}
            setStatus={setStatus}
            setExpiresIn={setExpiresIn}
            clientPortfolio={clientPortfolio}
            handlePortfolioSelection={handlePortfolioSelection}
            groupView={groupView}
            setGroupView={setGroupView}
            prefsStorageKey={prefsStorageKey}
            initialPrefs={initialPrefs}
          />
        }
        className="mt-6"
      >
        {dealModalOpen && (
          <DealModal
            open={dealModalOpen}
            id={task?.id}
            onCancel={handleDealModalCancel}
            onDeleteDeal={handleDeleteDeal}
          />
        )}
        {onClose && (
          <div style={{ textAlign: 'right', marginBottom: 3 }}>
            <Button type="primary" icon={<TableOutlined />} onClick={onClose}>
              Return to Table
            </Button>
          </div>
        )}
        <Spin spinning={loading} size="large" tip={loadingSpinner}>
          <GanttErrorBoundary>
            <BryntumGanttProjectModel
              tasks={tasks}
              resources={resources}
              assignments={assignments}
              ref={projectRef}
              startDate={project?.startDate}
              endDate={project?.endDate}
              {...projectConfig}
            />
            <Row>
              <Col flex="auto" className="b-toolbar b-outer flex b-dock-top">
                {mode == GanttChartMode.OWN_CALENDER &&
                  ACLCheck(['create:purchase_analysis_submission', 'create:site_wide_admin']) && (
                    <Button
                      type="default"
                      className="self-center font-semibold mx-1"
                      onClick={() => {
                        setIsDealTypeModalOpen(true);
                      }}
                    >
                      <PlusOutlined style={{ fontSize: '16px' }} />
                      Add
                    </Button>
                  )}
                {tasks.length > 0 && (
                  <span
                    className="self-center ml-4 flex items-center gap-3"
                    style={{ fontSize: '13px', color: '#555', fontWeight: 600 }}
                  >
                    <span className="flex items-center gap-1">
                      CONTRACTS
                      <Badge
                        count={tasksSummary.totalContracts}
                        overflowCount={99999}
                        style={{ backgroundColor: '#7C7C7C', fontSize: '11px' }}
                      />
                    </span>
                    {/* <span>
                      TCV <b>{FormatCurrency(tasksSummary.totalTcv, 0)}</b>
                    </span> */}
                  </span>
                )}
              </Col>

              <Col flex="none">
                <BryntumToolbar
                  items={RenewalGanttChartToolbarItems({
                    ganttRef,
                    tasks,
                    clientName: clientName || 'NPI',
                    groupView: groupView
                  })}
                />
              </Col>
            </Row>
            <BryntumGantt
              stateProvider={stateProvider}
              stateId={ganttStateId}
              listeners={{
                beforeStateApply: (event: any) => {
                  if (event.state && Array.isArray(event.state.columns)) {
                    event.state.columns = sanitizeGanttColumns(event.state.columns, earlyAccessStaleColumnIds);
                  }
                }
              }}
              ref={ganttRef}
              project={projectRef}
              onTaskClick={taskClickHandler}
              onTaskDblClick={() => false}
              onTaskResizeEnd={(event) =>
                console.log({
                  endDate: event.taskRecord.get('endDate')
                })
              }
              onPercentBarDrop={(event) =>
                console.log({
                  percentDone: event.taskRecord.get('percentDone')
                })
              }
              onTaskDrop={(event) =>
                console.log({
                  startDate: event.taskRecords[0].get('startDate'),
                  endDate: event.taskRecords[0].get('endDate')
                })
              }
              projectLinesFeature={false}
              taskMenuFeature={false}
              taskResizeFeature={{ dragThreshold: 1 }}
              features={{
                sort: { multiSort: true },
                taskTooltip: { template: customTaskTooltip || undefined } as Partial<TaskTooltipConfig>
              }}
              filterFeature={true}
              {...ganttConfig}
            />
          </GanttErrorBoundary>
        </Spin>
        {/* Below the Gantt, outside the Bryntum project — hidden deals are never Gantt tasks, so the
            CONTRACTS badge above and every TCV/date roll-up stay unaffected. Client calendar only. */}
        {mode === GanttChartMode.OWN_CALENDER && (
          <HiddenDealsSection
            hiddenDeals={hiddenDeals}
            onUnhide={handleUnhideDeal}
            onOpenDeal={(dealId) => navigate(`/deal_details/${dealId}`)}
          />
        )}
        {dealIdToAddTasks ? (
          <AddDealTaskForm
            handleCancel={() => {
              clearDealIdToAddTasks();
            }}
            handleDealSubmit={handleAddTasks}
          />
        ) : null}
        {isDealTypeModalOpen && <DealTypeModal onCancel={handleDealTypeModalCancel} />}
        {dealIdToEditTask ? (
          <EditTaskModal
            handleCancel={() => {
              clearDealIdToEditTask();
            }}
            handleEditTask={handleEditTask}
            handleDeleteTask={handleDeleteTask}
          />
        ) : null}
      </Card>
    </>
  );
};
